import {
  Box,
  Button,
  Modal,
  Stack,
  Typography,
  Divider,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { UserHQLocationDashBoardStore } from "../../../../../store/main/UserHQLocationDashBoardStore";

type AddConfirmHQLocationDashBoardModalProps = {
  open: boolean;
  location: {
    formatted_address: string;
    place_id: string;
    lat: number;
    lng: number;
  } | null;
  onConfirm: (headquarter_id: number, brick_id: number) => void;
  onCancel: () => void;
};

const AddConfirmHQLocationDashBoardModal = ({
  open,
  location,
  onConfirm,
  onCancel,
}: AddConfirmHQLocationDashBoardModalProps) => {
  const { fetchHQLocation, fetchBricksLocation } =
    UserHQLocationDashBoardStore();

  const [hqSearch, setHqSearch] = useState("");
  const [hqList, setHqList] = useState<any[]>([]);
  const [bricksList, setBricksList] = useState<any[]>([]);
  const [selectedHQ, setSelectedHQ] = useState<any | null>(null);
  const [selectedBrick, setSelectedBrick] = useState<any | null>(null);
  const [openHQDropdown, setOpenHQDropdown] = useState(false);
  const [hqError, setHqError] = useState(false);
  const [brickError, setBrickError] = useState(false);
  const [errors, setErrors] = useState<{ hq?: string; brick?: string }>({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingHQ, setLoadingHQ] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [initialHQList, setInitialHQList] = useState<any[]>([]);

  // Debounce HQ search
  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = hqSearch.trim();

      if (trimmed.length >= 2) {
        loadHQData(trimmed, 1, true);
        setOpenHQDropdown(true);
      } else {
        setHqList(initialHQList.slice(0, 10));
        setPage(1);
        setHasMore(initialHQList.length > 10);
        setOpenHQDropdown(true);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [hqSearch]);

  useEffect(() => {
    if (!open) return;
    fetchHQLocation({ q: "" }).then((res: any) => {
      if (res?.success) {
        setInitialHQList(res.headquarters);
        setHqList(res.headquarters.slice(0, 10));
      }
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSelectedHQ(null);
    setSelectedBrick(null);
    setErrors({});
    setHqSearch("");
    loadHQData("", 1, true);
  }, [open]);

  // HQ search logic
  useEffect(() => {
    if (!open) return;

    if (debouncedSearch === "" || debouncedSearch.length < 2) {
      setHqList([]);
      setPage(1);
      setHasMore(true);
      return;
    }
    loadHQData(debouncedSearch, 1, true);
  }, [debouncedSearch]);

  // Load bricks when HQ selected
  useEffect(() => {
    if (!selectedHQ) {
      setBricksList([]);
      setSelectedBrick(null);
      return;
    }

    fetchBricksLocation({
      headquarter_ids: [selectedHQ.id],
      station: "HQ",
    }).then((res: any) => {
      if (res?.success) setBricksList(res.bricks);
    });
  }, [selectedHQ]);

  const loadHQData = async (query: string, pageNum: number, reset = false) => {
    if (loadingHQ) return;

    setLoadingHQ(true);

    const res: any = await fetchHQLocation({ q: query });
    if (res?.success) {
      const results = res.headquarters || [];
      const start = (pageNum - 1) * 10;
      const end = pageNum * 10;
      const batch = results.slice(start, end);

      if (reset) {
        setHqList(batch);
      } else {
        setHqList((prev) => [...prev, ...batch]);
      }

      setHasMore(end < results.length);
    }

    setLoadingHQ(false);
  };

  const handleHQScroll = (event: any) => {
    const listboxNode = event.currentTarget;
    const bottom =
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 5;

    if (bottom && hasMore && !loadingHQ) {
      const nextPage = page + 1;
      setPage(nextPage);

      if (hqSearch.trim().length >= 2) {
        loadHQData(hqSearch.trim(), nextPage);
      } else {
        const start = (nextPage - 1) * 10;
        const end = nextPage * 10;
        const nextBatch = initialHQList.slice(start, end);
        setHqList((prev) => [...prev, ...nextBatch]);
        setHasMore(end < initialHQList.length);
      }
    }
  };

  const isConfirmDisabled = !selectedHQ || !selectedBrick;

  return (
    <Modal
      open={open}
      onClose={() => {}}
      sx={{ backdropFilter: "blur(0.1px)" }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 10,
          p: 4,
          width: { xs: "90%", sm: "50%", md: "35%" },
          maxWidth: 480,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Confirm Added HQ Location
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            textAlign: "left",
            mb: 2,
            p: 2,
            backgroundColor: "#f7f7f7",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            <strong>Address :</strong>
          </Typography>

          <Typography variant="body2">
            {location?.formatted_address || "No location selected"}
          </Typography>
        </Box>

        <Autocomplete
          value={selectedHQ}
          onChange={(_, value) => {
            setSelectedHQ(value);
            setHqError(false);

            if (!value) {
              // When cleared
              setHqSearch("");
              setHqList(initialHQList.slice(0, 10));
              setPage(1);
              setHasMore(initialHQList.length > 10);
              setOpenHQDropdown(true);
            } else {
              setHqSearch(value.name);
            }
          }}
          options={hqList}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.name || ""}
          inputValue={hqSearch}
          onInputChange={(_, value, reason) => {
            if (reason === "input") {
              setSelectedHQ(null);
              setHqSearch(value);
              setPage(1);
              setHasMore(true);

              if (value.trim().length >= 2) {
                loadHQData(value.trim(), 1, true);
                setOpenHQDropdown(true);
              } else {
                // show initial list on <2 letters
                setHqList(initialHQList.slice(0, 10));
                setPage(1);
                setHasMore(initialHQList.length > 10);
                setOpenHQDropdown(true);
              }
            }
          }}
          open={openHQDropdown && hqList.length > 0}
          onOpen={() => {
            if (!selectedHQ && hqSearch.trim().length < 2) {
              setHqList(initialHQList.slice(0, 10));
              setPage(1);
              setHasMore(initialHQList.length > 10);
            } else if (hqSearch.trim().length >= 2) {
              loadHQData(hqSearch.trim(), 1, true);
            }
            setOpenHQDropdown(true);
          }}
          onClose={() => setOpenHQDropdown(false)}
          ListboxProps={{
            onScroll: handleHQScroll,
            style: { maxHeight: 250, overflow: "auto" },
          }}
          noOptionsText={
            hqSearch.trim().length < 2
              ? "Type at least 2 letters"
              : loadingHQ
              ? "Loading..."
              : hqList.length === 0
              ? "No match found"
              : ""
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select HQ"
              placeholder="Search your HQ"
              size="small"
              error={hqError}
              helperText={hqError ? "Please select an HQ" : ""}
            />
          )}
          sx={{ mb: 2 }}
        />

        <Autocomplete
          value={selectedBrick}
          onChange={(_, value) => {
            setSelectedBrick(value);
            setBrickError(false);
          }}
          options={bricksList}
          getOptionLabel={(option) => option.name || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Brick"
              size="small"
              error={brickError}
              helperText={brickError ? "Please select a Brick" : ""}
            />
          )}
          sx={{ mb: 3 }}
        />

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setSelectedHQ(null);
              setSelectedBrick(null);
              setErrors({});
              onCancel();
            }}
            sx={{ px: 3, py: 1, borderRadius: 2 }}
          >
            CANCEL
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={isConfirmDisabled}
            onClick={() => {
              if (!selectedHQ) {
                setHqError(true);
                return;
              }
              if (!selectedBrick) {
                setBrickError(true);
                return;
              }

              onConfirm(selectedHQ.id, selectedBrick.id);
            }}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            CONFIRM
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default AddConfirmHQLocationDashBoardModal;
